import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, MapPin, CalendarDays, Users, ScanLine, ClipboardList, Pencil, Send, Loader2, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, downloadFile } from "@/lib/api";
import { fmtDate, fmtDateTime, EVENT_STATUS_CLS } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Badge, Loading, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { EventForm } from "@/components/EventForm";

export default function Events() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [sending, setSending] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(null);
  const downloadPdf = (e) => {
    setPdfBusy(e.id);
    downloadFile(`/events/${e.id}/summary.pdf`, `Riepilogo-${(e.code || e.name).replace(/[^A-Za-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}.pdf`)
      .catch((err) => toast.error(errMsg(err)))
      .finally(() => setPdfBusy(null));
  };
  const load = () => api.get("/events").then((r) => setRows(r.data));
  const sendSummary = async (e) => {
    setSending(e.id);
    try {
      const { data } = await api.post(`/events/${e.id}/summary`);
      toast.success(`Riepilogo inviato: ${data.sent} email su ${data.recipients} destinatari (${data.admitted} ammessi, ${data.denied} non ammessi)`);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSending(null);
    }
  };
  useEffect(() => { load(); }, []);
  const canEdit = ["admin", "sector_manager"].includes(user.role);
  const canScan = ["admin", "organizer"].includes(user.role);

  return (
    <div>
      <PageHeader eyebrow="Calendario" title="Manifestazioni sportive"
        subtitle={user.role === "organizer" ? "Manifestazioni per cui sei autorizzato a effettuare le verifiche." : "Gare di automobilismo, motociclismo e karting."}>
        {canEdit && <Button onClick={() => { setEditing(null); setForm(true); }} className="bg-[#0F2042] hover:bg-[#1E3A8A]" data-testid="events-new-btn"><Plus className="mr-2 h-4 w-4" />Nuova manifestazione</Button>}
      </PageHeader>
      {rows === null ? <Loading /> : rows.length === 0 ? <Empty>Nessuna manifestazione.</Empty> : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="events-list">
          {rows.map((e, i) => (
            <div key={e.id} className="card-surface flex flex-col p-5 fade-up transition-shadow hover:shadow-md" style={{ animationDelay: `${i * 40}ms` }} data-testid={`event-card-${e.id}`}>
              <div className="flex items-start justify-between gap-2">
                <span className="font-mono text-xs text-slate-500">{e.code}</span>
                <Badge cls={EVENT_STATUS_CLS[e.status]}>{e.status}</Badge>
              </div>
              <div className="mt-2 font-display text-lg font-semibold leading-snug text-slate-900">{e.name}</div>
              <div className="mt-1 text-sm font-medium text-blue-800">{e.discipline_name} · {e.sector_name}</div>
              <div className="mt-3 space-y-1.5 text-sm text-slate-600">
                <div className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-slate-400" />{fmtDate(e.start_date)}{e.end_date !== e.start_date && ` – ${fmtDate(e.end_date)}`}</div>
                <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" />{e.location || "—"}</div>
                <div className="flex items-center gap-2"><Users className="h-4 w-4 text-slate-400" /><span data-testid={`event-count-${e.id}`}>{e.participants_count}{e.max_participants ? ` / ${e.max_participants}` : ""} partecipanti</span></div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1">
                {e.allowed_categories.map((c) => <span key={c.id} className="rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-700">{c.name}</span>)}
              </div>
              <div className="mt-2 text-xs text-slate-500">Organizzatori: {e.organizers.map((o) => o.name).join(", ") || "nessuno assegnato"}</div>
              {e.summary_sent_at && <div className="mt-1 text-xs text-emerald-700" data-testid={`event-summary-sent-${e.id}`}>Riepilogo inviato il {fmtDateTime(e.summary_sent_at)}</div>}
              <div className="mt-auto flex flex-wrap gap-2 pt-4">
                {canScan && <Button size="sm" onClick={() => navigate(`/scanner?event=${e.id}`)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`event-scan-btn-${e.id}`}><ScanLine className="mr-1.5 h-4 w-4" />Scanner</Button>}
                <Button size="sm" variant="outline" onClick={() => navigate(`/partecipazioni?event=${e.id}`)} data-testid={`event-participants-btn-${e.id}`}><ClipboardList className="mr-1.5 h-4 w-4" />Partecipanti</Button>
                <Button size="sm" variant="outline" disabled={sending === e.id} onClick={() => sendSummary(e)} data-testid={`event-summary-btn-${e.id}`}>{sending === e.id ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />}Invia riepilogo</Button>
                <Button size="sm" variant="outline" disabled={pdfBusy === e.id} onClick={() => downloadPdf(e)} data-testid={`event-summary-pdf-btn-${e.id}`}>{pdfBusy === e.id ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileDown className="mr-1.5 h-4 w-4" />}Scarica PDF</Button>
                {canEdit && <Button size="sm" variant="ghost" onClick={() => { setEditing(e); setForm(true); }} data-testid={`event-edit-btn-${e.id}`}><Pencil className="mr-1.5 h-4 w-4" />Modifica</Button>}
              </div>
            </div>
          ))}
        </div>
      )}
      <EventForm open={form} onOpenChange={setForm} event={editing} onSaved={load} />
    </div>
  );
}
